import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BenjiKIV} from "../../BenjiKIV";
import {BenjiKIVGameAssets} from "../../BenjiKIVGameAssets";
import {BasicBitmapCountUp} from "../../components/BasicBitmapCountUp"
import {gsap} from "gsap";
import {SpineTween} from "@nolimitcity/slot-game/bin/animation/SpineTween";
import {BenjiKIVGameMode} from "../../server/BenjiKIVGameMode";
import {BenjiKIVSound} from "../../sound/BenjiKIVSound";
import {SpineUtils} from "../../utils/SpineUtils";
import {ParticleSettings} from "../../components/particles/ParticleSettings";
import {ParticleController} from "../../components/particles/ParticleController";
import {ScreenBounds} from "@nolimitcity/slot-launcher/bin/display/ScreenBounds";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";

/**
 * Created by Jerker Nord on 2023-01-11.
 */

export class BenjiKIVMaxWinView extends PIXI.Container implements StageLayer {

    private static COUNTUP_POSITION:PIXI.Point = new PIXI.Point(360, 360);
    private static ANIMATION_NAMES:string[] = ["Max_Win_1", "Max_Win_2", "Max_Win_3", "Max_Win_4", "Max_Win_End"];

    private _maxWinAnimation:PIXI.spine.Spine;
    private _maxWinCountUp:BasicBitmapCountUp;
    private _particleMoneyLoops:ParticleController[] = [];
    private _moneyRainContainer:PIXI.Container;
    private _startSpineContainer:PIXI.Container;
    private _endSpineContainer:PIXI.Container;
    private _background:PIXI.Sprite;

    constructor() {
        super();
        this.name = "BenjiKIVMaxWinView";

        this.init();
    }

    private init():void {
        this._background = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._background.tint = 0x000000;
        this._background.alpha = 0;
        this.addChild(this._background);

        this._maxWinAnimation = new PIXI.spine.Spine(BenjiKIV.assetLoader.getSpineAsset(BenjiKIVGameAssets.MAX_WIN_SPINE));
        this._maxWinAnimation.position.set(360, 360);
        this._maxWinAnimation.visible = false;
        this._startSpineContainer = new PIXI.Container();
        this._startSpineContainer.addChild(this._maxWinAnimation);
        this.addChild(this._startSpineContainer);

        this._moneyRainContainer = new PIXI.Container();
        this._particleMoneyLoops.push(BenjiKIV.particleManager.createAnimation(this._moneyRainContainer, "money1Loop", "money1", ParticleSettings.money1Config, true));
        this._particleMoneyLoops.push(BenjiKIV.particleManager.createAnimation(this._moneyRainContainer, "money2Loop", "money2", ParticleSettings.money2Config, true));
        this._particleMoneyLoops.push(BenjiKIV.particleManager.createAnimation(this._moneyRainContainer, "money3Loop", "money3", ParticleSettings.money3Config, true));
        this.addChild(this._moneyRainContainer);

        this._endSpineContainer = new PIXI.Container();
        this.addChild(this._endSpineContainer);

        this._maxWinCountUp = new BasicBitmapCountUp({
            fontName : BenjiKIVGameAssets.BITMAP_FONT_BIG_WIN_COUNT_UP,
            fontSize : 90,
            tint : 0xffffff
        }, 720, new PIXI.Point(0.5, 0.5));
        this._maxWinCountUp.visible = false;
        this._maxWinCountUp.position = BenjiKIVMaxWinView.COUNTUP_POSITION;
        this.addChild(this._maxWinCountUp);

        this.onResize();
    }

    private startMoneyRain():void {
        this._particleMoneyLoops[1].enableEmit(true);
        this._particleMoneyLoops[2].enableEmit(true);
    }

    private startFullMoneyRain():void {
        this._particleMoneyLoops[0].enableEmit(true);
    }

    private stopMoneyRain():void {
        this._particleMoneyLoops.forEach((p:ParticleController) => {
            p.enableEmit(false);
        });
    }

    public getMaxWinTL(readyToSkip:Function):gsap.core.Timeline {
        const maxWinTL:gsap.core.Timeline = gsap.timeline();
        maxWinTL.add(() => {BenjiKIV.soundPlayer.fadeMusic(1, 0, 1700)});
        maxWinTL.add(() => {BenjiKIV.soundPlayer.playSound(BenjiKIVSound.MAX_WIN_START)});
        maxWinTL.add(() => {this._background.alpha = 0});
        maxWinTL.add(() => {gsap.to(this._background, {duration : 0.4, alpha : 0.85})});
        maxWinTL.add(() => {
            const countUpTL:gsap.core.Timeline = gsap.timeline({delay : 4.562});
            countUpTL.add(() => {this._maxWinCountUp.visible = true});
            countUpTL.add(this._maxWinCountUp.getCountAnimation(0, (BenjiKIV.model.gameData.playedBetValue * 10000), 5.187));
            countUpTL.add(gsap.timeline({delay : 1.250}).add(() => {
                this._endSpineContainer.addChild(this._maxWinAnimation);
                this.startFullMoneyRain();
                this._maxWinAnimation.state.setAnimation(0, BenjiKIVMaxWinView.ANIMATION_NAMES[4], true);
                BenjiKIV.soundPlayer.playSound(BenjiKIVSound.MAX_WIN_MUSIC, 1, 10000);
                this._maxWinCountUp.visible = false;
                BenjiKIV.keypadPlugin.setWin(BenjiKIV.model.gameData.accumulatedRoundWin, BenjiKIV.model.showTotalWin, BenjiKIV.isWinBelowStake());
                readyToSkip();
            }));
        });
        maxWinTL.add(() => {
            this._maxWinAnimation.visible = true;
            SpineUtils.resetSpineAnimation(this._maxWinAnimation);
        })
        maxWinTL.add(new SpineTween(this._maxWinAnimation, 0, BenjiKIVMaxWinView.ANIMATION_NAMES[0]));
        maxWinTL.add(new SpineTween(this._maxWinAnimation, 0, BenjiKIVMaxWinView.ANIMATION_NAMES[1]));
        maxWinTL.add(new SpineTween(this._maxWinAnimation, 0, BenjiKIVMaxWinView.ANIMATION_NAMES[2]));
        maxWinTL.add([
            () => {this._maxWinAnimation.state.setAnimation(0, BenjiKIVMaxWinView.ANIMATION_NAMES[3], true)}
        ]);
        maxWinTL.add(() => this.startMoneyRain(), "+=0.5");
        return maxWinTL;
    }

    public onResize() {
        const bounds:ScreenBounds = NolimitApplication.screenBounds;
        const y:number = (0 - ((bounds.height - 720) / 2));
        const x:number = (0 - ((bounds.width - 720) / 2));
        this._particleMoneyLoops.forEach((p:ParticleController) => {
            p.emitter.cleanup();
            p.emitter.spawnPos.y = y;
            p.emitter.spawnPos.x = x;
            p.emitter.spawnRect.width = bounds.width;
        });

        this._background.width = NolimitApplication.screenBounds.width;
        this._background.height = NolimitApplication.screenBounds.height;
        this._background.x = NolimitApplication.screenBounds.left;
        this._background.y = NolimitApplication.screenBounds.top;
    }

    public endMaxWin():void {
        this.stopMoneyRain();
        BenjiKIV.soundPlayer.stopSound(BenjiKIVSound.MAX_WIN_MUSIC);
        BenjiKIV.soundPlayer.playSound(BenjiKIVSound.BIG_WIN_MUSIC_END);
        BenjiKIV.soundPlayer.switchMusic(BenjiKIVGameMode.NORMAL);
        BenjiKIV.soundPlayer.fadeMusic(0, 1, 1500);
        this._maxWinAnimation.visible = false;
        this._startSpineContainer.addChild(this._maxWinAnimation);
        SpineUtils.resetSpineAnimation(this._maxWinAnimation);
        BenjiKIV.keypadPlugin.setZeroBetSpinCounter(-1);
        BenjiKIV.backgroundElements.setMode(BenjiKIVGameMode.NORMAL);
        BenjiKIV.xBetView.show();
        gsap.to(this._background, {duration : 0.2, alpha : 0});
    }
}